import React, { useState, useEffect, useRef } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function TypeOfRecordSelect({ value, onChange, label = 'Type / Records', disabled }) {
  const [query, setQuery] = useState(value || '');
  const [options, setOptions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newType, setNewType] = useState('');
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const token = localStorage.getItem('token');

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setShowDropdown(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => { setQuery(value || ''); }, [value]);

  const search = async (q) => {
    setLoading(true);
    try {
      const url = q
        ? `${API_URL}/api/type-of-records?q=${encodeURIComponent(q)}`
        : `${API_URL}/api/type-of-records?q=medical`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setOptions(Array.isArray(data) ? data.slice(0, 8) : []);
    } catch { setOptions([]); }
    setLoading(false);
  };

  const handleInput = (e) => {
    const v = e.target.value;
    setQuery(v);
    setShowDropdown(true);
    search(v);
    onChange(v);
  };

  const select = (opt) => {
    setQuery(opt.name);
    setShowDropdown(false);
    onChange(opt.name);
  };

  const addNew = async () => {
    if (!newType.trim()) return;
    try {
      const res = await fetch(`${API_URL}/api/type-of-records`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: newType.trim() })
      });
      const created = await res.json();
      select(created);
      setShowAddForm(false);
      setNewType('');
    } catch(err) { console.error(err); }
  };

  return (
    <div ref={ref} className="relative">
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <div className="relative">
        <input
          value={query}
          onChange={handleInput}
          onFocus={() => { setShowDropdown(true); search(query); }}
          disabled={disabled}
          placeholder="Type to search or select..."
          className={`w-full px-2 py-1.5 pr-8 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${disabled ? 'bg-gray-50' : ''}`}
        />
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); setShowDropdown(v => !v); if (!showDropdown) search(query); }}
          disabled={disabled}
          className="absolute right-1 top-1/2 -translate-y-1/2 px-1 text-gray-400 hover:text-gray-600"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>
      {showDropdown && (
        <div className="absolute z-50 w-full bg-white border border-gray-200 rounded shadow-lg mt-1 max-h-56 overflow-y-auto">
          {loading && <div className="px-3 py-2 text-xs text-gray-400">Searching...</div>}
          {!loading && options.map(opt => (
            <div key={opt.id} onMouseDown={() => select(opt)} className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm">
              {opt.name}
            </div>
          ))}
          {!loading && options.length === 0 && <div className="px-3 py-2 text-xs text-gray-400">No matches — type to search</div>}
          <div className="border-t px-3 py-2">
            {!showAddForm ? (
              <button onMouseDown={() => setShowAddForm(true)} className="text-xs text-blue-600 hover:underline">+ Add New Type</button>
            ) : (
              <div className="flex gap-1">
                <input value={newType} onChange={e => setNewType(e.target.value)} placeholder="New type name" className="flex-1 px-2 py-1 text-xs border rounded" />
                <button onMouseDown={addNew} className="px-2 py-1 text-xs bg-blue-600 text-white rounded">Add</button>
                <button onMouseDown={() => setShowAddForm(false)} className="px-2 py-1 text-xs bg-gray-200 rounded">✕</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
